'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetPositionsRow } from '@morfi/backend-sdk-v2/client';
import { isI64 } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { Skeleton } from '@/components/ui/app-skeleton';
import { DataListItem, type DataListItemMetric } from '@/components/ui/data-list-item';
import { onEvent } from '@/events';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { UserPortfolioPositionDisplayRow } from '@/hooks/positions/user-portfolio/display-types';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { useFormattedCurrency } from '@/lib/currency';
import { pnlColorClass } from '@/lib/market/position-pnl';
import { useMarketDetailPrefetchIntent } from '@/lib/sdk/prefetch';
import { DURATION_POSITION_SMALL_MS } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { PortfolioPositionShareButton } from './portfolio-position-actions';
import { PortfolioPositionResultBadge } from './portfolio-position-result-badge';
type PositionListItemProps = {
  row: UserPortfolioPositionDisplayRow;
  mode: 'open';
  isOwnPortfolio: boolean;
} | {
  row: GetPositionsRow;
  mode: 'closed';
  isOwnPortfolio: boolean;
  claimReady?: boolean;
};
export function PortfolioPositionListItem(props) {
  const $ = _c(87);
  const {
    row,
    mode,
    isOwnPortfolio
  } = props;
  const t = useTranslations("profile.stats.positions");
  const tFee = useTranslations("trade.fees");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const label = row.outcomeLabel ?? "";
  let t0;
  if ($[0] !== label || $[1] !== row.outcomeIndex) {
    t0 = {
      title: label,
      index: row.outcomeIndex
    };
    $[0] = label;
    $[1] = row.outcomeIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== row.market) {
    t1 = {
      marketPubkey: row.market
    };
    $[3] = row.market;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const color = useOutcomeColor(t0, t1);
  const winner = row.positionType === "winner";
  const href = winner ? `/winners/${row.outcomeMint}` : `/markets/${row.market}`;
  const intent = useIntentRoutePrefetch(href, useMarketDetailPrefetchIntent(winner ? null : row.market).onMouseEnter);
  let currentValueMetric;
  let realisedMetric;
  let t2;
  let unrealised;
  if ($[5] !== formatD6 || $[6] !== formatD6Signed || $[7] !== mode || $[8] !== props.mode || $[9] !== props.row || $[10] !== row.costBasisD6 || $[11] !== row.realizedCostBasisD6 || $[12] !== row.realizedPenaltyD6 || $[13] !== row.realizedPnlD6 || $[14] !== t || $[15] !== tFee) {
    const realised = moneyWire(row.realizedPnlD6);
    const value = props.mode === "open" ? props.row.displayMark.currentValueD6 : null;
    unrealised = props.mode === "open" ? props.row.displayMark.unrealisedPnlD6 : null;
    const invested = moneyWire(mode === "open" ? row.costBasisD6 : row.realizedCostBasisD6);
    const penalty = moneyWire(row.realizedPenaltyD6);
    let t3;
    if ($[20] !== t) {
      t3 = t("value");
      $[20] = t;
      $[21] = t3;
    } else {
      t3 = $[21];
    }
    let t4;
    if ($[22] !== formatD6 || $[23] !== value) {
      t4 = amount(value, formatD6);
      $[22] = formatD6;
      $[23] = value;
      $[24] = t4;
    } else {
      t4 = $[24];
    }
    let t5;
    if ($[25] !== t3 || $[26] !== t4) {
      t5 = {
        key: "value",
        label: t3,
        value: t4
      };
      $[25] = t3;
      $[26] = t4;
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    currentValueMetric = t5;
    let t6;
    if ($[28] !== t) {
      t6 = t("realised");
      $[28] = t;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    const t7 = amount(realised, formatD6Signed, true);
    let t8;
    if ($[30] !== t6 || $[31] !== t7) {
      t8 = {
        key: "realised",
        label: t6,
        value: t7
      };
      $[30] = t6;
      $[31] = t7;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    realisedMetric = t8;
    let t9;
    if ($[33] !== t) {
      t9 = t("invested");
      $[33] = t;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    const t10 = amount(invested, formatD6);
    let t11;
    if ($[35] !== t10 || $[36] !== t9) {
      t11 = {
        key: "invested",
        label: t9,
        value: t10
      };
      $[35] = t10;
      $[36] = t9;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    let t12;
    if ($[38] !== currentValueMetric || $[39] !== formatD6Signed || $[40] !== mode || $[41] !== t || $[42] !== unrealised) {
      t12 = mode === "open" ? [currentValueMetric, {
        key: "unrealised",
        label: t("unrealised"),
        value: amount(unrealised, formatD6Signed, true)
      }] : [];
      $[38] = currentValueMetric;
      $[39] = formatD6Signed;
      $[40] = mode;
      $[41] = t;
      $[42] = unrealised;
      $[43] = t12;
    } else {
      t12 = $[43];
    }
    t2 = [t11, ...t12, realisedMetric, ...(penalty !== null && penalty > BigInt(0) ? [{
      key: "penalty",
      label: tFee("penalty_fee"),
      value: amount(penalty, formatD6)
    }] : [])];
    $[5] = formatD6;
    $[6] = formatD6Signed;
    $[7] = mode;
    $[8] = props.mode;
    $[9] = props.row;
    $[10] = row.costBasisD6;
    $[11] = row.realizedCostBasisD6;
    $[12] = row.realizedPenaltyD6;
    $[13] = row.realizedPnlD6;
    $[14] = t;
    $[15] = tFee;
    $[16] = currentValueMetric;
    $[17] = realisedMetric;
    $[18] = t2;
    $[19] = unrealised;
  } else {
    currentValueMetric = $[16];
    realisedMetric = $[17];
    t2 = $[18];
    unrealised = $[19];
  }
  const metrics = t2;
  let t3;
  if ($[44] !== isOwnPortfolio || $[45] !== row.market || $[46] !== row.outcomeIndex) {
    t3 = function recordClick() {
      if (isOwnPortfolio) {
        onEvent("portfolio_position_sell_clicked", {
          marketPubkey: row.market,
          outcomeIndex: row.outcomeIndex
        });
      }
    };
    $[44] = isOwnPortfolio;
    $[45] = row.market;
    $[46] = row.outcomeIndex;
    $[47] = t3;
  } else {
    t3 = $[47];
  }
  const recordClick = t3;
  const t4 = `${label} · ${row.marketTitle ?? row.market}`;
  let t5;
  if ($[48] !== label) {
    t5 = <span className="truncate">{label}</span>;
    $[48] = label;
    $[49] = t5;
  } else {
    t5 = $[49];
  }
  let t6;
  if ($[50] !== row.resultState) {
    t6 = <PortfolioPositionResultBadge resultState={row.resultState} />;
    $[50] = row.resultState;
    $[51] = t6;
  } else {
    t6 = $[51];
  }
  let t7;
  if ($[52] !== t5 || $[53] !== t6) {
    t7 = <span className="flex min-w-0 items-center gap-2">{t5}{t6}</span>;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
  } else {
    t7 = $[54];
  }
  const t8 = row.marketTitle ?? row.market;
  let t9;
  if ($[55] !== color.hue || $[56] !== row.market || $[57] !== row.outcomeImageNsfw || $[58] !== row.outcomeImageUrl || $[59] !== row.outcomeIndex) {
    t9 = <OptimizedImage variant="thumbnail" entity={{
      kind: "outcome",
      marketPubkey: row.market,
      outcomeIndex: row.outcomeIndex,
      hue: color.hue,
      imageUrl: row.outcomeImageUrl,
      nsfw: row.outcomeImageNsfw
    }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
    $[55] = color.hue;
    $[56] = row.market;
    $[57] = row.outcomeImageNsfw;
    $[58] = row.outcomeImageUrl;
    $[59] = row.outcomeIndex;
    $[60] = t9;
  } else {
    t9 = $[60];
  }
  let t10;
  if ($[61] !== color.text) {
    t10 = cn("size-3 rounded-full bg-current", color.text);
    $[61] = color.text;
    $[62] = t10;
  } else {
    t10 = $[62];
  }
  let t11;
  if ($[63] !== t10) {
    t11 = <span className={t10} />;
    $[63] = t10;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  const t12 = mode === "open" ? currentValueMetric : realisedMetric;
  let t13;
  if ($[65] !== href || $[66] !== label || $[67] !== mode || $[68] !== row.market || $[69] !== row.marketTitle || $[70] !== row.outcomeIndex || $[71] !== row.realizedPnlD6 || $[72] !== row.wallet || $[73] !== unrealised) {
    t13 = row.wallet.length > 0 ? <PortfolioPositionShareButton destinationPath={href} marketPubkey={row.market} marketTitle={row.marketTitle ?? row.market} walletPubkey={row.wallet} outcomeIndex={row.outcomeIndex} outcomeTitle={label} closed={mode === "closed"} heroPnlD6={mode === "open" ? unrealised?.toString() : row.realizedPnlD6} /> : undefined;
    $[65] = href;
    $[66] = label;
    $[67] = mode;
    $[68] = row.market;
    $[69] = row.marketTitle;
    $[70] = row.outcomeIndex;
    $[71] = row.realizedPnlD6;
    $[72] = row.wallet;
    $[73] = unrealised;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  let t14;
  if ($[75] !== href || $[76] !== intent || $[77] !== metrics || $[78] !== recordClick || $[79] !== t11 || $[80] !== t12 || $[81] !== t13 || $[82] !== t4 || $[83] !== t7 || $[84] !== t8 || $[85] !== t9) {
    t14 = <DataListItem testId="portfolio-position-list-item" href={href} intent={intent} onClick={recordClick} ariaLabel={t4} primaryText={t7} secondaryText={t8} image={t9} imageBadge={t11} metrics={metrics} compactMetric={t12} action={t13} />;
    $[75] = href;
    $[76] = intent;
    $[77] = metrics;
    $[78] = recordClick;
    $[79] = t11;
    $[80] = t12;
    $[81] = t13;
    $[82] = t4;
    $[83] = t7;
    $[84] = t8;
    $[85] = t9;
    $[86] = t14;
  } else {
    t14 = $[86];
  }
  return t14;
}
function amount(value: bigint | null, format: (wire: string) => string, signed: boolean = false): ReactNode {
  if (value === null) return <Skeleton className="h-5 w-16" />;
  return <span className={signed ? pnlColorClass(value) : undefined}>
      <AnimatedBigInt value={value} duration={DURATION_POSITION_SMALL_MS} format={(current: bigint): string => format(current.toString())} />
    </span>;
}
function moneyWire(value: string): bigint | null {
  if (!/^-?(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}